import {
  createRouter,
  createWebHashHistory,
  type RouteRecordRaw,
} from "vue-router";
import EmployeeSecurity from "@/enumerations/Security/EmployeeSecurity";
import ContractsSecuritySettings from "@/helpers/Security/ContractsSecuritySettings";
import PaymentsSecuritySettings from "@/helpers/Security/PaymentsSecuritySettings";
import PurchasesSecuritySettings from "@/helpers/Security/PurchasesSecuritySettings";
import OrdersSecuritySettings from "@/helpers/Security/OrdersSecuritySettings";
import { useCommoditySecuritySettings } from "@/composables/Security/useCommoditySecuritySettings";
import { useAccountSecuritySettings } from "@/composables/Security/useAccountSecuritySettings";
import { useAlertsStore } from "@/stores/alerts";
import { useAuthStore } from "@/stores/auth";
import { useParametersStore } from "@/stores/parameters";
import accountRoutes from "@/router/account";
import contractsRoutes from "@/router/contracts";
import commodityRoutes from "@/router/commodity";
import managerRoutes from "@/router/manager";
import miscRoutes from "@/router/misc";
import oilEnergyRoutes from "@/router/oilEnergy";
import ordersRoutes from "@/router/orders";
import paymentsRoutes from "@/router/payments";
import purchasesRoutes from "@/router/purchases";
import OilEnergySecuritySettings from "@/helpers/Security/OilEnergySecuritySettings";
import { useMiscSecuritySettings } from "@/composables/Security/useMiscSecuritySettings";
import { usePortalSecuritySettings } from "@/composables/Security/usePortalSecuritySettings";
import { trackPageView } from "@/services/activity/portalActivityTracker";

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    redirect: "/home",
  },
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/Login/LoginRouteView.vue"),
    meta: {
      title: "Login",
      skipAuth: true,
    },
  },
  {
    path: "/resetPassword",
    name: "reset-password",
    component: () => import("@/views/Login/ResetPasswordRouteView.vue"),
    meta: {
      title: "Reset Password",
      skipAuth: true,
    },
  },
  {
    path: "/home",
    name: "home",
    component: () => import("@/views/Home/HomeRouteView.vue"),
    meta: {
      title: "Home",
      requiresAuth: true,
    },
  },
  ...accountRoutes,
  ...contractsRoutes,
  ...commodityRoutes,
  ...managerRoutes,
  ...miscRoutes,
  ...oilEnergyRoutes,
  ...ordersRoutes,
  ...paymentsRoutes,
  ...purchasesRoutes,
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    redirect: "/home",
  },
];

const router = createRouter({
  history: createWebHashHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach(async (to, from) => {
  const alertsStore = useAlertsStore();
  const authStore = useAuthStore();
  const parametersStore = useParametersStore();

  alertsStore.clearAlert();
  authStore.restoreSession();

  if (!parametersStore.isLoaded) {
    await parametersStore.loadParameterSettings();
  }

  if (
    authStore.isAuthenticated &&
    authStore.webLogin.isEmployee &&
    authStore.webLogin.employeeSecurity === EmployeeSecurity.userProfile &&
    !authStore.userProfileBasedSecurity
  ) {
    await authStore.loadUserProfileBasedSecurity();
  }

  if (
    authStore.isAuthenticated &&
    parametersStore.parameterSettings.useEntities &&
    authStore.selectedAccount?.entityId &&
    !parametersStore.isEntityParametersLoaded
  ) {
    await parametersStore.loadEntityParameterSettings();
  }

  //Every Oil & Energy right folds in whether Merchant flags the customer for Oil & Energy at all, so the answer
  //has to be in hand before the guards below judge it - otherwise a grower opening one of those pages directly,
  //or arriving on a restored session, is sent back to the home page and the menu group stays hidden. It returns
  //immediately once answered for the selected account, so this is one read per account, not per navigation.
  if (authStore.isAuthenticated && !authStore.isOilEnergyResolved) {
    await authStore.loadOilEnergyEligibility();
  }

  if (
    !authStore.isAuthenticated &&
    (to.meta.requiresAuth || to.path === "/home")
  ) {
    return {
      path: "/login",
      query: {
        redirect: to.fullPath,
      },
    };
  }

  if (authStore.isAuthenticated && to.name === "login") {
    return {
      path: "/home",
    };
  }

  if (authStore.isAuthenticated && to.meta.accountPermission) {
    const accountSecuritySettings = useAccountSecuritySettings();
    const accountPermission = String(to.meta.accountPermission);

    const isAllowed =
      accountPermission === "summary"
        ? accountSecuritySettings.showAccountSummaryPage.value
        : accountPermission === "addresses"
          ? accountSecuritySettings.showAddressesPage.value
          : accountPermission === "buyers"
            ? accountSecuritySettings.showBuyersPage.value
        : accountPermission === "profile"
          ? accountSecuritySettings.showProfilePage.value
          : accountPermission === "loginHistory"
            ? accountSecuritySettings.showLoginHistoryPage.value
            : accountPermission === "patronage"
              ? accountSecuritySettings.showPatronagePage.value
              : true;

    if (!isAllowed) {
      return {
        path: "/home",
      };
    }
  }

  if (authStore.isAuthenticated && to.meta.purchasesPermission) {
    const purchasesSecuritySettings = PurchasesSecuritySettings.getInstance();
    const purchasesPermission = String(to.meta.purchasesPermission);

    const isAllowed =
      purchasesPermission === "viewPurchases"
        ? purchasesSecuritySettings.showViewPurchasesPage
        : purchasesPermission === "creditActivity"
          ? purchasesSecuritySettings.showViewCreditActivityPage
          : purchasesPermission === "transactions"
            ? purchasesSecuritySettings.showViewTransactionsPage
            : purchasesPermission === "statements"
              ? purchasesSecuritySettings.showViewStatementsPage
              : purchasesPermission === "purchaseSummary"
                ? purchasesSecuritySettings.showViewPurchaseSummaryPage
                : purchasesPermission === "fuelPurchaseSummary"
                  ? purchasesSecuritySettings.showViewFuelPurchaseSummaryPage
                  : purchasesPermission === "upcomingDeliveries"
                    ? purchasesSecuritySettings.showViewUpcomingDeliveriesPage
                    : true;

    if (!isAllowed) {
      return {
        path: "/home",
      };
    }
  }

  if (authStore.isAuthenticated && to.meta.oilEnergyPermission) {
    const oilEnergySecuritySettings = OilEnergySecuritySettings.getInstance();

    const isAllowed = oilEnergySecuritySettings.canShowPage(
      String(to.meta.oilEnergyPermission)
    );

    if (!isAllowed) {
      return {
        path: "/home",
      };
    }
  }

  if (authStore.isAuthenticated && to.meta.contractsPermission) {
    const contractsSecuritySettings = ContractsSecuritySettings.getInstance();
    const contractsPermission = String(to.meta.contractsPermission);

    const isAllowed =
      contractsPermission === "viewBookings"
        ? contractsSecuritySettings.showViewBookingsPage
        : contractsPermission === "viewPrepaids"
          ? contractsSecuritySettings.showViewPrepaidsPage
          : true;

    if (!isAllowed) {
      return {
        path: "/home",
      };
    }
  }

  if (authStore.isAuthenticated && to.meta.commodityPermission) {
    const commoditySecuritySettings = useCommoditySecuritySettings();
    const commodityPermission = String(to.meta.commodityPermission);

    const isAllowed =
      commodityPermission === "cashBids"
        ? commoditySecuritySettings.showCashBidsPage.value
        : commodityPermission === "balances"
          ? commoditySecuritySettings.showBalancesPage.value
          : commodityPermission === "activity"
            ? commoditySecuritySettings.showActivityPage.value
            : commodityPermission === "contracts"
              ? commoditySecuritySettings.showContractsPage.value
              : commodityPermission === "offers"
                ? commoditySecuritySettings.showOffersPage.value
                : commodityPermission === "tickets"
                  ? commoditySecuritySettings.showTicketsPage.value
                  : commodityPermission === "grainBank"
                    ? commoditySecuritySettings.showGrainBankPage.value
                    : commodityPermission === "proofOfYield"
                      ? commoditySecuritySettings.showProofOfYieldPage.value
                      : true;

    if (!isAllowed) {
      return {
        path: "/home",
      };
    }
  }

  if (authStore.isAuthenticated && to.meta.ordersPermission) {
    const ordersSecuritySettings = OrdersSecuritySettings.getInstance();
    const ordersPermission = String(to.meta.ordersPermission);

    const isAllowed =
      ordersPermission === "viewOrders"
        ? ordersSecuritySettings.showViewOrdersPage
        : ordersPermission === "createOrder"
          ? ordersSecuritySettings.showCreateOrderPage
        : true;

    if (!isAllowed) {
      return {
        path: "/home",
      };
    }
  }

  if (authStore.isAuthenticated && to.meta.managerRequired) {
    const portalSecuritySettings = usePortalSecuritySettings();

    if (!portalSecuritySettings.managerAccountSelected.value) {
      return {
        path: "/home",
      };
    }
  }

  if (authStore.isAuthenticated && to.meta.miscPermission) {
    const miscSecuritySettings = useMiscSecuritySettings();
    const miscPermission = String(to.meta.miscPermission);

    const isAllowed =
      miscPermission === "contact"
        ? miscSecuritySettings.showContactUsPage.value
        : miscPermission === "licenses"
          ? miscSecuritySettings.showLicensesPage.value
          : miscPermission === "fieldAlytics"
            ? miscSecuritySettings.showFieldAlyticsPage.value
            : true;

    if (!isAllowed) {
      return {
        path: "/home",
      };
    }
  }

  if (authStore.isAuthenticated && to.meta.paymentsPermission) {
    const paymentsSecuritySettings = PaymentsSecuritySettings.getInstance();
    const paymentsPermission = String(to.meta.paymentsPermission);

    const isAllowed =
      paymentsPermission === "createPayment"
        ? paymentsSecuritySettings.showCreatePaymentPage
        : paymentsPermission === "autoPayments"
        ? paymentsSecuritySettings.showAutoPaymentsPage
        : paymentsPermission === "pendingPayments"
        ? paymentsSecuritySettings.showPendingPaymentsPage
        : paymentsPermission === "paymentSources"
          ? paymentsSecuritySettings.showPaymentSourcesPage
        : paymentsPermission === "paymentHistory"
        ? paymentsSecuritySettings.showPaymentHistoryPage
        : true;

    if (!isAllowed) {
      return {
        path: "/home",
      };
    }
  }

  if (authStore.webLogin.forcePasswordChange && to.name !== "reset-password") {
    return {
      path: "/resetPassword",
    };
  }

  if (to.name === "reset-password") {
    const hasResetToken =
      typeof to.query.token === "string" && to.query.token.trim().length > 0;

    if (authStore.webLogin.forcePasswordChange || hasResetToken) {
      return true;
    }

    if (authStore.isAuthenticated) {
      return true;
    }

    return {
      path: from.path || "/login",
    };
  }

  return true;
});

router.afterEach((to, from, failure) => {
  const title = to.meta.title
    ? `Grower Portal | ${String(to.meta.title)}`
    : "Grower Portal";

  document.title = title;

  // A route name, not the path, so record keys in a path never split one page into
  // many; a query change within the same page is not a new visit.
  if (!failure && to.name && to.name !== from.name) {
    trackPageView(String(to.name), to.meta);
  }
});

export default router;
